import React from 'react'
import _ from 'underscore'
import { Receipt } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import Button from '../../../../../button'
import Currency from '../../../../../currency'
import FinaliseOrder from '../../../../orders/finalise'

const HR = 'my-4 border-t'
const TOTAL_LABEL = 'text-lg font-semibold'

class PricingCalculatorSummary extends React.Component {

  renderBundleDiscount(discount_amount) {
    const outputs = this.props.pricing_calculator.outputs

    if (outputs.discount_percentage > 0) {
      return (
        <div className="flex justify-between">
          <span className="text-left">Bundle discount <Badge variant="destructive" className="font-bold">{outputs.discount_percentage}%</Badge></span>
          <span className="text-right">-<Currency value={discount_amount} currency={outputs.currency_code} placeholder='-' /></span>
        </div>
      )
    }

    return null
  }

  renderDiscretionaryDiscount(discount_amount) {
    const outputs = this.props.pricing_calculator.outputs

    if (outputs.discretionary_discount_percentage > 0) {
      return (
        <div className="flex justify-between">
          <span className="text-left">Special offer <Badge variant="destructive" className="font-bold">{outputs.discretionary_discount_percentage}%</Badge></span>
          <span className="text-right">-<Currency value={discount_amount} currency={outputs.currency_code} placeholder='-' /></span>
        </div>
      )
    }

    return null
  }

  renderMonthlyPaymentTotals() {
    const outputs = this.props.pricing_calculator.outputs

    return (
      <div>
        <div className="flex justify-between">
          <div>
            Base Rate
          </div>
          <div className="text-right">
            <Currency value={outputs.per_month.base_amount} currency={outputs.currency_code} placeholder='-' />
          </div>
        </div>
        {this.renderBundleDiscount(outputs.per_month.discount)}
        {this.renderDiscretionaryDiscount(outputs.per_month.discretionary_discount)}
        <hr className={HR} />
        <div className="flex justify-between">
          <h4 className={TOTAL_LABEL}>Monthly Total</h4>
          <h4 className={`${TOTAL_LABEL} text-right`}>
            <Currency value={outputs.per_month.amount} currency={outputs.currency_code} placeholder='-' />
          </h4>
        </div>
      </div>
    )
  }

  renderFullPaymentUpfrontTotals() {
    const outputs = this.props.pricing_calculator.outputs

    return (
      <div>
        <div className="flex justify-between">
          <div>
            Base Rate
          </div>
          <div className="text-right">
            <Currency value={outputs.per_package.base_amount} currency={outputs.currency_code} placeholder='-' />
          </div>
        </div>
        {this.renderBundleDiscount(outputs.per_package.discount)}
        {this.renderDiscretionaryDiscount(outputs.per_package.discretionary_discount)}
        <hr className={HR} />
        <div className="flex justify-between">
          <h4 className={TOTAL_LABEL}>Package Total</h4>
          <h4 className={`${TOTAL_LABEL} text-right`}>
            <Currency value={outputs.per_package.amount} currency={outputs.currency_code} placeholder='-' />
          </h4>
        </div>
      </div>
    )
  }

  renderEftInstalmentsTotals() {
    const inputs = this.props.pricing_calculator.inputs
    const outputs = this.props.pricing_calculator.outputs

    return (
      <div>
        <div className="flex justify-between">
          <div>
            Base Rate
          </div>
          <div className="text-right">
            <Currency value={outputs.per_package.base_amount} currency={outputs.currency_code} placeholder='-' />
          </div>
        </div>
        {this.renderBundleDiscount(outputs.per_package.discount)}
        {this.renderDiscretionaryDiscount(outputs.per_package.discretionary_discount)}
        <hr className={HR} />
        <div className="flex justify-between font-semibold">
          <div>
            Package Total
          </div>
          <div className="text-right">
            <Currency value={outputs.per_package.amount} currency={outputs.currency_code} placeholder='-' />
          </div>
        </div>
        <div className="flex justify-between">
          <div>
            Number of Instalments
          </div>
          <div className="text-right">
            {inputs.number_of_instalments}
          </div>
        </div>
        <div className="flex justify-between">
          <h4 className={TOTAL_LABEL}>Total per Instalment</h4>
          <h4 className={`${TOTAL_LABEL} text-right`}>
            <Currency value={outputs.per_month.amount} currency={outputs.currency_code} placeholder='-' />
          </h4>
        </div>
      </div>
    )
  }

  renderUpdateAndFinaliseButton() {
    return (
      <div>
        <Button className="btn btn-success btn-block btn-lg mb-2" onClick={this.props.onUpdateOrder}>
          Update & Finalise
        </Button>
      </div>
    )
  }

  renderSaveButton() {
    return (
      <Button className="btn btn-success btn-block btn-lg mb-2" onClick={this.props.onSaveLessonOrder}>
        Save
      </Button>
    )
  }

  renderFinaliseOrder() {
    return (
      <FinaliseOrder
        lesson_order={this.props.lesson_order}
        onAddToOrder={this.props.onAddToOrder}
        onPlaceOrder={this.props.onPlaceOrder}
      />
    )
  }

  renderButtons() {
    if (this.props.lesson_order.attached_to_order) {
      return this.props.lesson_order.actions.can_edit && this.renderUpdateAndFinaliseButton()
    } else {
      return (
        <div className="mb-2">
          {this.props.change_applied ? this.renderSaveButton() : this.renderFinaliseOrder()}
        </div>
      )
    }
  }

  render() {
    const inputs = this.props.pricing_calculator.inputs
    const outputs = this.props.pricing_calculator.outputs

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="size-4"/>
            Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(['monthly_credit_card','monthly_debit_order','monthly_subscription_manual'].includes(inputs.payment_term)) ?
            this.renderMonthlyPaymentTotals()
            : null
          }
          {(inputs.payment_term === 'full_payment_upfront') ?
            this.renderFullPaymentUpfrontTotals()
            : null
          }
          {(inputs.payment_term === 'eft_instalments') ?
            this.renderEftInstalmentsTotals()
            : null
          }
          <div className="my-4 text-[13px] text-muted-foreground">
            <div className="flex justify-between">
              <span>Cost per Hour</span>
              <span className="text-right"><Currency value={outputs.per_hour.amount} currency={outputs.currency_code} placeholder='-' /></span>
            </div>
            <div className="flex justify-between">
              <span>Cost per Lesson</span>
              <span className="text-right"><Currency value={outputs.per_lesson.amount} currency={outputs.currency_code} placeholder='-' /></span>
            </div>
            <div className="flex justify-between">
              <span>Total Lessons</span>
              <span className="text-right">{outputs.total_lessons || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span>Total Hours</span>
              <span className="text-right">{outputs.total_hours || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span>Should Last Until</span>
              <span className="text-right">{outputs.should_last_until || '-'}</span>
            </div>
          </div>
          {this.renderButtons()}
        </CardContent>
      </Card>
    )
  }
}

export default PricingCalculatorSummary
